import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  Search,
  Loader2,
  AlertCircle,
  FileText,
  Plus,
  Check,
  Pencil,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useUsersNeedingAction,
  useCrmUsersByIds,
  useCrmUserSearch,
  useCrmUserNin,
  useAllInternalUserComments,
  useAllInternalNotes,
  useBiometrics,
  useBiometricPrices,
  useAllPatientImpersonators,
  useLabs,
} from "@/hooks/api";
import {
  resolveVisitLab,
  buildLabsById,
} from "@/lib/utils/resolveVisitLab";
import { b2bFlowLabel } from "@/lib/utils/b2bFlowLabel";
import {
  bloodTestCompanyNames,
  surveyCompletionLabel,
} from "@/pages/anamnesis/blood-test-overview";
import { buildFurstPriceMap, sumFurstCost } from "@/lib/utils/furstCost";
import {
  useRequisitionedForUsers,
  useLabVisitsByIds,
  useReadyForSubmissionRequisitions,
  useRecentlySubmittedRequisitions,
  useAwaitingSnailMailRequisitions,
  useRequisitionBiometricsMap,
  useB2bLabVisitsByIds,
  useUpdateLabVisit,
  useSendRequisition,
  useConfirmFurstNotReceived,
  type FurstAddressOverride,
} from "@/hooks/api/useLabVisits";
import { useAuth } from "@/lib/auth";
import { useDoctorMessageThreadCount } from "@/hooks/api/useDoctorMessages";
import { DoctorMessagesTab } from "@/components/doctor-messages/threads-tab";
import { furstSendState, type FurstSendState } from "@/lib/utils/furstResend";
import { LabVisitsService } from "@/lib/api_client";
import type {
  UsersNeedingAction,
  LabVisit,
  LabVisitRequisition,
  CrmUser,
  Biometric,
} from "@/lib/api_client";
import { getAuthHeaders } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { ImpersonationReasonPicker } from "@/components/shared/impersonation-reason-picker";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MaskedText } from "@/components/masked-text";
import { EtterrekvireringModal } from "@/components/shared/etterrekvirering-modal";
import { cn } from "@/lib/cn";

// ── Tab definitions ───────────────────────────────────────────

const TABS = [
  { key: "alle", label: "Alle", statuses: null },
  {
    key: "analyse",
    label: "Analyse",
    statuses: ["allBiometricResultsReceived"],
  },
  { key: "blodprove", label: "Blodprøve", statuses: ["bloodTestRequested"] },
  {
    key: "rekvirering",
    label: "Rekvirering",
    statuses: ["pendingRequisition"],
  },
  {
    key: "under-arbeid",
    label: "Under arbeid",
    statuses: ["requisitioned", "awaitingResults"],
  },
  // statuses: null → falls back to the full customer list for anyone who
  // hand-navigates here without clinical access (the tab itself is hidden).
  { key: "meldinger", label: "Pasientdialog", statuses: null },
] as const;

type TabKey = (typeof TABS)[number]["key"];

// ── Helpers ──────────────────────────────────────────────────

function getStatusText(status: string | null | undefined): string {
  switch (status) {
    case "bloodTestRequested":
      return "Trenger biomarkørforslag";
    case "pendingRequisition":
      return "Venter på rekvisisjon";
    case "requisitioned":
      return "Rekvirert";
    case "awaitingResults":
      return "Venter på resultater";
    case "allBiometricResultsReceived":
      return "Trenger analyse og anbefaling";
    case "assessmentCompleted":
      return "Analyse fullført";
    default:
      return "Trenger handling";
  }
}

function getStatusVariant(
  status: string | null | undefined,
): "default" | "secondary" | "info" | "outline" {
  switch (status) {
    case "allBiometricResultsReceived":
      return "info";
    case "pendingRequisition":
    case "bloodTestRequested":
      return "default";
    case "assessmentCompleted":
      return "secondary";
    default:
      return "outline";
  }
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes}m siden`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}t siden`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d siden`;
  const months = Math.floor(days / 30);
  return `${months}mnd siden`;
}

function getAddress(crmUser: CrmUser, isSnailMail: boolean): string {
  if (!isSnailMail) return "Digital rekvirering";
  const addr = crmUser.address as {
    street?: string;
    postalCode?: string;
    city?: string;
  } | null;
  if (!addr || typeof addr !== "object") return "Adresse ikke tilgjengelig";
  const parts = [
    addr.street,
    [addr.postalCode, addr.city].filter(Boolean).join(" "),
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "Adresse ikke tilgjengelig";
}

/**
 * Drops blank fields from an address override, and the whole thing if nothing
 * is left. Keeps empty strings out of the request body so the backend treats
 * untouched fields as "keep folkeregister's value" rather than "blank it".
 */
function nonEmptyOverride(
  override: FurstAddressOverride | undefined,
): FurstAddressOverride | undefined {
  if (!override) return undefined;
  const entries = Object.entries(override).filter(
    ([, value]) => typeof value === "string" && value.trim() !== "",
  );
  if (entries.length === 0) return undefined;
  return Object.fromEntries(
    entries.map(([key, value]) => [key, (value as string).trim()]),
  ) as FurstAddressOverride;
}

/**
 * Folkeregister's postnummer is four zero-padded digits ("0155"), and FURST
 * validates it against poststed. The backend rejects anything else too, but
 * catching it here means the admin sees the rule while the field is still in
 * front of them, instead of after a round-trip.
 */
function postnummerError(
  override: FurstAddressOverride | undefined,
): string | null {
  const value = override?.postnummer?.trim();
  if (!value) return null;
  return /^\d{4}$/.test(value) ? null : "Postnummer må være fire siffer";
}

function calculateRequisitionCost(
  bioIds: Set<string>,
  allBiometrics: Biometric[],
  priceMap: Map<string, number>,
): number {
  const bioMap = new Map(allBiometrics.map((b) => [b.id, b]));
  return sumFurstCost(bioIds, bioMap, priceMap);
}

// ── Types ────────────────────────────────────────────────────

interface UserRow {
  authUserId: string;
  fullName: string;
  email: string;
  healthSurveyCompleted?: boolean | null;
  actions: UsersNeedingAction[];
  oldestActionDate: number;
}

interface RequisitionRow {
  requisition: LabVisitRequisition;
  labVisit: LabVisit;
  crmUser: CrmUser;
  biomarkerNames: string[];
  biomarkerIds: Set<string>;
  cost: number;
}

type GroupPosition = "only" | "first" | "middle" | "last";

/**
 * Tailwind classes for a row in a (possibly) grouped table. Single-visit
 * rows get the usual bottom-border separator. Multi-row visit groups
 * share a left band and drop the bottom border between paired rows so
 * they read as a single block.
 */
function groupedRowClasses(
  pos: GroupPosition,
  baseBorderClass: string,
): string {
  const band = pos === "only" ? "" : "border-l-2 border-l-admin-primary/50";
  const bottom =
    pos === "first" || pos === "middle" ? "" : `border-b ${baseBorderClass}`;
  return `${band} ${bottom} last:border-b-0`.trim();
}

/**
 * Reorders rows so requisitions on the same lab visit are adjacent
 * (HELFO partition first within a visit), preserving first-seen visit
 * order so user-chosen sorts upstream still drive which visit appears
 * where. Annotates each row with its position in the visit-group so the
 * table can render a shared left band and drop inner borders between
 * paired rows.
 */
function groupRowsByVisit(
  rows: RequisitionRow[],
): Array<RequisitionRow & { groupPosition: GroupPosition }> {
  const byVisit = new Map<string, RequisitionRow[]>();
  const visitOrder: string[] = [];
  for (const row of rows) {
    const lvId = row.requisition.labVisitId;
    if (!byVisit.has(lvId)) {
      byVisit.set(lvId, []);
      visitOrder.push(lvId);
    }
    byVisit.get(lvId)!.push(row);
  }
  const out: Array<RequisitionRow & { groupPosition: GroupPosition }> = [];
  for (const lvId of visitOrder) {
    const group = byVisit.get(lvId)!;
    // Sort a copy so we don't mutate the map's stored array. Cheap (2
    // entries per group at most) and keeps the helper side-effect-free.
    const sorted = group.slice().sort((a, b) =>
      a.requisition.helfo === b.requisition.helfo
        ? 0
        : a.requisition.helfo
          ? -1
          : 1,
    );
    for (let i = 0; i < sorted.length; i++) {
      const row = sorted[i];
      if (!row) continue;
      const pos: GroupPosition =
        sorted.length === 1
          ? "only"
          : i === 0
            ? "first"
            : i === sorted.length - 1
              ? "last"
              : "middle";
      out.push({ ...row, groupPosition: pos });
    }
  }
  return out;
}

function SortIcon({
  col,
  sortState,
}: {
  col: string;
  sortState: { col: string; asc: boolean };
}) {
  if (sortState.col !== col) return null;
  return sortState.asc ? (
    <ChevronUp className="inline h-3 w-3 ml-0.5" />
  ) : (
    <ChevronDown className="inline h-3 w-3 ml-0.5" />
  );
}

// ── Shared requisition table layout ──────────────────────────
//
// All three requisition tables (Rekvirering, Venter på manuell utsendelse,
// Nylig rekvirert) share the same 8 data columns in the same order. Only the
// trailing action column differs. Sort headers are opt-in.

const REQ_COL_KEYS = [
  "name",
  "helfo",
  "clinical",
  "lab",
  "address",
  "bio",
  "cost",
  "date",
] as const;
type ReqColKey = (typeof REQ_COL_KEYS)[number];

const REQ_COL_HEADERS: Record<
  ReqColKey,
  { label: string; align?: "right"; sortable?: boolean }
> = {
  name: { label: "Kunde", sortable: true },
  helfo: { label: "Helfo" },
  clinical: { label: "Kliniske opplysninger" },
  lab: { label: "Lab", sortable: true },
  address: { label: "Adresse" },
  bio: { label: "Bio.", sortable: true },
  cost: { label: "Kostnad", align: "right", sortable: true },
  date: { label: "Sendt", sortable: true },
};

function RequisitionTableHeaders({
  actionLabel = "",
  borderClass = "border-admin-card-border",
  sort,
}: {
  actionLabel?: string;
  borderClass?: string;
  sort?: {
    state: { col: string; asc: boolean };
    toggle: (col: string) => void;
  };
}) {
  return (
    <tr className={`border-b ${borderClass}`}>
      {REQ_COL_KEYS.map((key) => {
        const def = REQ_COL_HEADERS[key];
        const align = def.align === "right" ? "text-right" : "text-left";
        const isClickable = sort && def.sortable;
        return (
          <th
            key={key}
            onClick={isClickable ? () => sort.toggle(key) : undefined}
            className={`${align} text-xs font-medium text-admin-text-subtle px-2 py-1.5 ${
              isClickable ? "cursor-pointer select-none" : ""
            }`}
          >
            {def.label}
            {isClickable ? <SortIcon col={key} sortState={sort.state} /> : null}
          </th>
        );
      })}
      <th className="text-right text-xs font-medium text-admin-text-subtle px-2 py-1.5">
        {actionLabel}
      </th>
    </tr>
  );
}

function formatShortDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1).toString().padStart(2, "0")}.${d.getFullYear().toString().slice(2)}`;
}

/**
 * Fødselsnummer is no longer on the crm_user row: fetching it is a deliberate,
 * audit-logged act, because it is normally only needed when writing a manual
 * requisition for one patient.
 */
function NinCell({ authUserId }: { authUserId: string | null }) {
  const [requested, setRequested] = useState(false);
  const { data: nin, isFetching, isSuccess, isError, refetch } = useCrmUserNin(
    authUserId,
    requested
  );

  // user_profiles.nin is nullable, so a resolved lookup can legitimately have
  // no number. Say so rather than falling back to the button, which would
  // invite clicks that each write another audit row for the same empty answer.
  if (isSuccess) {
    return (
      <span className="text-admin-text-secondary">
        {nin ? <MaskedText value={nin} type="nin" /> : "Fnr mangler"}
      </span>
    );
  }

  return (
    <button
      type="button"
      disabled={!authUserId || isFetching}
      onClick={() => (requested ? refetch() : setRequested(true))}
      title="Henter fødselsnummer. Oppslaget logges."
      className="text-xs text-admin-text-subtle underline decoration-dotted underline-offset-2 hover:text-admin-primary transition-colors cursor-pointer disabled:cursor-default disabled:opacity-50"
    >
      {isFetching ? "Henter…" : isError ? "Fnr utilgjengelig" : "Vis fnr"}
    </button>
  );
}

function RequisitionDataCells({
  row,
  impersonating,
  onKundeClick,
  onBiomarkerClick,
  splitVisitIds,
  isSnailMail,
}: {
  row: RequisitionRow;
  impersonating: string | null;
  onKundeClick: (row: RequisitionRow) => void;
  onBiomarkerClick: (names: string[]) => void;
  splitVisitIds?: Set<string>;
  isSnailMail: boolean;
}) {
  const isSplit = splitVisitIds?.has(row.requisition.labVisitId) ?? false;
  const comment = row.requisition.clinicalInfo;
  const truncated =
    comment && comment.length > 60 ? comment.slice(0, 60) + "…" : comment;
  return (
    <>
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-2 text-sm whitespace-nowrap">
          <button
            className="text-left hover:text-admin-primary transition-colors cursor-pointer"
            onClick={() => onKundeClick(row)}
          >
            <span className="font-medium">
              <MaskedText
                value={[row.crmUser.givenName, row.crmUser.familyName]
                  .filter(Boolean)
                  .join(" ")}
              />
            </span>
            {row.crmUser.email && (
              <span className="text-admin-text-secondary ml-2">
                <MaskedText value={row.crmUser.email} type="email" />
              </span>
            )}
            {impersonating === (row.crmUser.authUserId ?? row.crmUser.id) && (
              <Loader2 className="inline ml-2 h-3 w-3 animate-spin" />
            )}
          </button>
          <NinCell authUserId={row.crmUser.authUserId ?? row.crmUser.id} />
        </div>
      </td>
      <td className="px-2 py-1.5 whitespace-nowrap">
        <div className="inline-flex items-center gap-1">
          {/* Reserve the HELFO slot on non-HELFO rows of split visits so
              the Splittet badge below aligns with the one on the HELFO
              row above. Non-split visits don't need a placeholder. */}
          {(row.requisition.helfo || isSplit) && (
            <Badge className={cn(!row.requisition.helfo && "invisible")}>
              Ja
            </Badge>
          )}
          {isSplit && (
            <Badge
              variant="outline"
              className="text-admin-info border-admin-info/40"
              title="Lab-besøket har både HELFO- og privat-rekvisisjon"
            >
              Splittet
            </Badge>
          )}
        </div>
      </td>
      <td className="px-2 py-1.5 max-w-[180px]">
        {!comment ? (
          <span className="text-xs text-admin-text-subtle">—</span>
        ) : (
          <div className="relative group/req-comment inline-block">
            <span className="text-xs text-admin-text-secondary cursor-default">
              {truncated}
            </span>
            {comment.length > 60 && (
              <div className="absolute bottom-full left-0 mb-1.5 z-50 hidden group-hover/req-comment:block w-64 bg-admin-bg border border-admin-card-border rounded px-2.5 py-2 text-xs text-admin-text-primary shadow-lg whitespace-pre-wrap">
                {comment}
              </div>
            )}
          </div>
        )}
      </td>
      <td className="px-2 py-1.5 text-sm text-admin-text-secondary whitespace-nowrap">
        {row.crmUser.labName ?? "—"}
      </td>
      <td className="px-2 py-1.5 text-sm text-admin-text-secondary whitespace-nowrap">
        {getAddress(row.crmUser, isSnailMail) || "—"}
        {isSnailMail && (
          <Badge
            variant="outline"
            className="ml-1 text-xs text-admin-warn-400 border-admin-warn-400/40"
          >
            Brevpost
          </Badge>
        )}
      </td>
      <td className="px-2 py-1.5">
        {row.biomarkerNames.length > 0 ? (
          <button
            className="text-xs text-admin-primary hover:underline cursor-pointer"
            onClick={() => onBiomarkerClick(row.biomarkerNames)}
          >
            {row.biomarkerNames.length} stk
          </button>
        ) : (
          <span className="text-xs text-admin-text-subtle">0</span>
        )}
      </td>
      <td className="px-2 py-1.5 text-right text-sm text-admin-text-secondary whitespace-nowrap">
        {row.cost > 0 ? `${Math.round(row.cost)} kr` : "—"}
      </td>
      <td className="px-2 py-1.5 text-xs text-admin-text-secondary whitespace-nowrap">
        {formatShortDate(row.requisition.submittedAt)}
      </td>
    </>
  );
}

// ── Page Component ───────────────────────────────────────────

export function AnamnesisPage() {
  const { hasRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab: TabKey = hasRole("doctor") ? "analyse" : "alle";
  // Pasientdialog is admin-only for now. Hide it — and ignore a hand-typed
  // `?tab=meldinger` — for everyone else.
  const canSeeMessages = hasRole("admin");
  const requestedTab = searchParams.get("tab") as TabKey | null;
  const activeTab =
    requestedTab && (requestedTab !== "meldinger" || canSeeMessages)
      ? requestedTab
      : defaultTab;
  const isRekvireringTab = activeTab === "rekvirering";
  const isBloodTestTab = activeTab === "blodprove";
  const isMessagesTab = activeTab === "meldinger";
  const { data: awaitingReplyCount = 0 } = useDoctorMessageThreadCount({
    status: "open",
    awaitingReply: true,
    enabled: canSeeMessages,
  });

  const setActiveTab = (tab: TabKey) => {
    setSearchParams({ tab }, { replace: true });
  };

  const {
    data: actions,
    isLoading: actionsLoading,
    error: actionsError,
  } = useUsersNeedingAction();
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  useEffect(() => () => clearTimeout(searchTimer.current), []);
  const handleSearchChange = (value: string) => {
    setSearch(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(value), 300);
  };
  const [showOnlyNeedsAction, setShowOnlyNeedsAction] = useState(false);
  const [impersonating, setImpersonating] = useState<string | null>(null);
  const [reasonDialogRow, setReasonDialogRow] = useState<UserRow | null>(null);
  const [reason, setReason] = useState("");

  const { data: allUserComments } = useAllInternalUserComments();
  const { data: allInternalNotes } = useAllInternalNotes();
  const { data: impersonatorsByUser } = useAllPatientImpersonators();

  // Requisition-centric data (for Rekvirering tab + Nylig rekvirert).
  // Requisitions are now the primary unit — lab visits are looked up by ID
  // only for the authUserId join.
  //
  // Only the Rekvirering tab renders any of it, and it is the most expensive
  // thing the page does: three scans whose ids feed two lab-visit lookups and
  // three biometrics lookups, each of those batched and paginated. The other
  // four tabs used to pay for all of it and display none of it.
  const { data: readyRequisitions, isLoading: readyLoading } =
    useReadyForSubmissionRequisitions(isRekvireringTab);
  const { data: recentRequisitions } =
    useRecentlySubmittedRequisitions(isRekvireringTab);
  const { data: awaitingSnailMailRequisitions } =
    useAwaitingSnailMailRequisitions(isRekvireringTab);

  const readyReqIds = useMemo(
    () => (readyRequisitions ?? []).map((r) => r.id),
    [readyRequisitions],
  );
  const recentReqIds = useMemo(
    () => (recentRequisitions ?? []).map((r) => r.id),
    [recentRequisitions],
  );
  const awaitingSnailMailReqIds = useMemo(
    () => (awaitingSnailMailRequisitions ?? []).map((r) => r.id),
    [awaitingSnailMailRequisitions],
  );
  // Two lookups rather than one union: the id set is part of
  // useLabVisitsByIds' query key, so a union lets the two all-history queries
  // — which land about a second late — re-key the lookup the send queue reads
  // and blank its table until the wider fetch returns.
  const readyVisitIds = useMemo(() => {
    const set = new Set<string>();
    for (const r of readyRequisitions ?? []) set.add(r.labVisitId);
    return Array.from(set);
  }, [readyRequisitions]);
  const otherVisitIds = useMemo(() => {
    const set = new Set<string>();
    for (const r of recentRequisitions ?? []) set.add(r.labVisitId);
    for (const r of awaitingSnailMailRequisitions ?? []) set.add(r.labVisitId);
    return Array.from(set);
  }, [recentRequisitions, awaitingSnailMailRequisitions]);
  const { data: readyLabVisits } = useLabVisitsByIds(readyVisitIds);
  const { data: otherLabVisits } = useLabVisitsByIds(otherVisitIds);
  const labVisitMap = useMemo(() => {
    const map = new Map<string, LabVisit>();
    for (const lv of readyLabVisits ?? []) map.set(lv.id, lv);
    for (const lv of otherLabVisits ?? []) map.set(lv.id, lv);
    return map;
  }, [readyLabVisits, otherLabVisits]);

  // Roster rows for the users this page renders: everyone on the worklist
  // plus the users behind the requisitions on screen. Anyone else is reached
  // through the search box, which asks the server.
  const rosterIds = useMemo(() => {
    const set = new Set<string>();
    for (const a of actions ?? []) if (a.authUserId) set.add(a.authUserId);
    for (const lv of labVisitMap.values()) set.add(lv.authUserId);
    return [...set];
  }, [actions, labVisitMap]);
  const {
    data: crmUsers,
    isLoading: crmLoading,
    error: crmError,
  } = useCrmUsersByIds(rosterIds);
  const searchActive = debouncedSearch.trim().length >= 2;
  const { data: searchHits, error: searchError } =
    useCrmUserSearch(debouncedSearch);
  const { data: readyBiometricsMap } = useRequisitionBiometricsMap(readyReqIds);
  const { data: recentBiometricsMap } =
    useRequisitionBiometricsMap(recentReqIds);
  const { data: awaitingSnailMailBiometricsMap } = useRequisitionBiometricsMap(
    awaitingSnailMailReqIds,
  );
  // Both badge lookups below decorate the user table, which the Rekvirering
  // tab replaces with its own table — hence the empty id list there, which
  // disables the query rather than fetching rows nothing will read.
  //
  // Users whose rows can appear in the user table: the roster, plus search
  // hits, which the server answers from outside the roster.
  const badgeUserIds = useMemo(() => {
    if (isRekvireringTab) return [];
    const set = new Set(rosterIds);
    for (const u of searchHits ?? []) if (u.authUserId) set.add(u.authUserId);
    return [...set];
  }, [isRekvireringTab, rosterIds, searchHits]);
  // Visits the status badges can be asked about — the badge hangs off an
  // action, so the worklist is the whole set.
  const badgeVisitIds = useMemo(() => {
    if (isRekvireringTab) return [];
    const set = new Set<string>();
    for (const a of actions ?? []) if (a.labVisitId) set.add(a.labVisitId);
    return [...set];
  }, [isRekvireringTab, actions]);
  const { data: allRequisitioned } = useRequisitionedForUsers(badgeUserIds);
  const { data: activeB2bLabVisits } = useB2bLabVisitsByIds(badgeVisitIds);
  const { data: biometrics } = useBiometrics();
  const { data: biometricPrices } = useBiometricPrices();
  const { data: labs } = useLabs();
  const updateLabVisit = useUpdateLabVisit();

  // Visit-scoped lab resolver: `visit.labId` wins (B2B sets it at
  // creation; lifecycle snapshots profile.labId at requisitioned), with
  // fallback to profile.labId for visits that haven't been requisitioned
  // yet. Prevents the page from showing snail-mail UI for a B2B visit
  // whose company lab differs from the user's profile preference (or
  // vice versa). See lib/utils/resolveVisitLab.ts.
  const labsById = useMemo(() => buildLabsById(labs), [labs]);
  const isSnailMailForRow = useCallback(
    (row: { labVisit: LabVisit; crmUser: CrmUser }) => {
      return !!resolveVisitLab(row.labVisit, row.crmUser.labId, labsById)
        ?.snailMailRequisition;
    },
    [labsById]
  );

  const priceMap = useMemo(
    () => buildFurstPriceMap(biometricPrices ?? []),
    [biometricPrices],
  );

  const [apiModalOpen, setApiModalOpen] = useState(false);
  const [etterrekvireringRow, setEtterrekvireringRow] =
    useState<RequisitionRow | null>(null);
  const [actionModalRow, setActionModalRow] = useState<RequisitionRow | null>(
    null,
  );
  const [biomarkerModalNames, setBiomarkerModalNames] = useState<
    string[] | null
  >(null);

  // Sort state for recently requisitioned table
  const [recentSort, setRecentSort] = useState<{ col: string; asc: boolean }>({
    col: "date",
    asc: false,
  });
  const toggleSort = (col: string) =>
    setRecentSort((prev) => ({
      col,
      asc: prev.col === col ? !prev.asc : true,
    }));

  // Index user comments by authUserId
  const commentsByUserId = useMemo(() => {
    const map = new Map<string, typeof allUserComments>();
    for (const comment of allUserComments ?? []) {
      const list = map.get(comment.authUserId) ?? [];
      list.push(comment);
      map.set(comment.authUserId, list);
    }
    return map;
  }, [allUserComments]);

  // Index internal notes by labVisitId
  const notesByLabVisitId = useMemo(() => {
    const map = new Map<string, typeof allInternalNotes>();
    for (const note of allInternalNotes ?? []) {
      const list = map.get(note.labVisitId) ?? [];
      list.push(note);
      map.set(note.labVisitId, list);
    }
    return map;
  }, [allInternalNotes]);

  // Index active requisitions (status=requisitioned) by authUserId
  const requisitionedByUserId = useMemo(() => {
    const map = new Map<string, LabVisit>();
    for (const lv of allRequisitioned ?? []) {
      if (lv.authUserId) map.set(lv.authUserId, lv);
    }
    return map;
  }, [allRequisitioned]);

  // Index active B2B lab visits by labVisitId with the suffix the action badge
  // shows. Flows that run a distinct pipeline are named ("B2B:BHT",
  // "B2B:Kickstart") so the list says at a glance which one a row belongs to;
  // healthCheck and consumer stay the plain "B2B".
  const b2bBadgeLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const lv of activeB2bLabVisits ?? []) {
      map.set(lv.id, b2bFlowLabel(lv.b2bFlowType) ?? "B2B");
    }
    return map;
  }, [activeB2bLabVisits]);

  // Group actions by authUserId (exclude "paid" — not actionable yet)
  const actionsByUser = useMemo(() => {
    const map = new Map<string, UsersNeedingAction[]>();
    for (const a of actions ?? []) {
      if (!a.authUserId || a.status === "paid") continue;
      const list = map.get(a.authUserId) ?? [];
      list.push(a);
      map.set(a.authUserId, list);
    }
    return map;
  }, [actions]);

  // A CRM row enriched with its actions
  const toRow = useCallback(
    (u: CrmUser): UserRow => {
      const userActions =
        (u.authUserId ? actionsByUser.get(u.authUserId) : null) ?? [];
      const dates = userActions
        .map((a) => a.latestUpdate)
        .filter((d): d is string => d != null)
        .map((d) => new Date(d).getTime());
      return {
        authUserId: u.authUserId ?? u.id ?? "",
        fullName:
          u.fullName ??
          ([u.givenName, u.familyName].filter(Boolean).join(" ") || "Ukjent"),
        email: u.email ?? "",
        healthSurveyCompleted: u.healthSurveyCompleted,
        actions: userActions,
        oldestActionDate: dates.length > 0 ? Math.min(...dates) : Infinity,
      };
    },
    [actionsByUser],
  );
  const allRows: UserRow[] = useMemo(
    () => (crmUsers ?? []).map(toRow),
    [crmUsers, toRow],
  );
  const searchRows: UserRow[] = useMemo(
    () => (searchHits ?? []).map(toRow),
    [searchHits, toRow],
  );

  // Tab-based filtering
  const tabDef = TABS.find((t) => t.key === activeTab) ?? TABS[0];

  const filtered = useMemo(() => {
    let list = searchActive ? searchRows : allRows;

    if (showOnlyNeedsAction) {
      list = list.filter((r) => r.actions.length > 0);
    }

    // Filter by tab statuses
    if (tabDef.statuses) {
      const statusSet: Set<string> = new Set(tabDef.statuses);
      list = list.filter((r) =>
        r.actions.some((a) => a.status != null && statusSet.has(a.status)),
      );
    }

    // Sort: users with actions by oldest action first, then users without actions alphabetically
    list.sort((a, b) => {
      if (a.actions.length > 0 && b.actions.length > 0) {
        return a.oldestActionDate - b.oldestActionDate;
      }
      if (a.actions.length > 0) return -1;
      if (b.actions.length > 0) return 1;
      return a.fullName.localeCompare(b.fullName);
    });

    return list;
  }, [allRows, searchRows, searchActive, showOnlyNeedsAction, tabDef]);

  // Requisition rows (for Rekvirering tab)
  const crmUserMap = useMemo(() => {
    const map = new Map<string, CrmUser>();
    for (const u of crmUsers ?? []) {
      if (u.authUserId) map.set(u.authUserId, u);
      if (u.id) map.set(u.id, u);
    }
    return map;
  }, [crmUsers]);

  const buildRow = (
    requisition: LabVisitRequisition,
    bioMap: Map<string, Biometric>,
    biomarkerIdSource: string[] | undefined,
  ): RequisitionRow | null => {
    const labVisit = labVisitMap.get(requisition.labVisitId);
    if (!labVisit) return null;
    const crmUser = crmUserMap.get(labVisit.authUserId);
    if (!crmUser) return null;
    const ids = new Set(biomarkerIdSource ?? []);
    const names = [...ids]
      .map((id) => bioMap.get(id)?.name ?? id)
      .sort((a, b) => a.localeCompare(b, "nb"));
    return {
      requisition,
      labVisit,
      crmUser,
      biomarkerNames: names,
      biomarkerIds: ids,
      cost: requisition.helfo
        ? 0
        : calculateRequisitionCost(ids, biometrics ?? [], priceMap),
    };
  };

  const requisitionRows: RequisitionRow[] = useMemo(() => {
    if (!readyRequisitions || !crmUsers || !biometrics) return [];
    const bioMap = new Map(biometrics.map((b) => [b.id, b]));
    return readyRequisitions
      .map((req) => buildRow(req, bioMap, readyBiometricsMap?.get(req.id)))
      .filter((r): r is RequisitionRow => r !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    readyRequisitions,
    crmUsers,
    biometrics,
    crmUserMap,
    labVisitMap,
    priceMap,
    readyBiometricsMap,
  ]);

  // labVisitIds that have BOTH a helfo=true and a helfo=false non-cancelled
  // requisition visible across the row sets. A pure count would over-flag
  // because the same submitted requisition appears in both
  // useRecentlySubmittedRequisitions and useAwaitingSnailMailRequisitions
  // (overlapping status filters → one row appears twice in the count).
  // Tracking the set of helfo flags per labVisitId is the actual
  // "is this a split visit?" question.
  const splitVisitIds = useMemo(() => {
    const flagsByVisit = new Map<string, Set<boolean>>();
    const bump = (lvId: string, helfo: boolean) => {
      const flags = flagsByVisit.get(lvId) ?? new Set<boolean>();
      flags.add(helfo);
      flagsByVisit.set(lvId, flags);
    };
    for (const r of readyRequisitions ?? []) bump(r.labVisitId, r.helfo);
    for (const r of recentRequisitions ?? []) bump(r.labVisitId, r.helfo);
    for (const r of awaitingSnailMailRequisitions ?? [])
      bump(r.labVisitId, r.helfo);
    const split = new Set<string>();
    for (const [id, flags] of flagsByVisit) {
      if (flags.has(true) && flags.has(false)) split.add(id);
    }
    return split;
  }, [readyRequisitions, recentRequisitions, awaitingSnailMailRequisitions]);

  // "Nylig rekvirert": submitted-or-later requisitions in the last 7 days.
  // Snail-mail rows still at pendingRequisition are surfaced separately
  // (see awaitingManualConfirmationRows) and excluded here to avoid dupes.
  const recentRows: RequisitionRow[] = useMemo(() => {
    if (!recentRequisitions || !crmUsers || !biometrics) return [];
    const bioMap = new Map(biometrics.map((b) => [b.id, b]));
    return recentRequisitions
      .map((req) => buildRow(req, bioMap, recentBiometricsMap?.get(req.id)))
      .filter((r): r is RequisitionRow => r !== null)
      .filter(
        (r) =>
          !(
            isSnailMailForRow(r) &&
            r.labVisit.status === "pendingRequisition"
          ),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    recentRequisitions,
    crmUsers,
    biometrics,
    crmUserMap,
    labVisitMap,
    priceMap,
    recentBiometricsMap,
    isSnailMailForRow,
  ]);

  // Snail-mail labs awaiting manual postal sending. No time window: these
  // rows are blocking work and must stay visible until an admin clicks
  // "Sett rekvirert", however long ago the requisition was submitted.
  const awaitingManualConfirmationRows: RequisitionRow[] = useMemo(() => {
    if (!awaitingSnailMailRequisitions || !crmUsers || !biometrics) return [];
    const bioMap = new Map(biometrics.map((b) => [b.id, b]));
    return awaitingSnailMailRequisitions
      .map((req) =>
        buildRow(req, bioMap, awaitingSnailMailBiometricsMap?.get(req.id)),
      )
      .filter((r): r is RequisitionRow => r !== null)
      .filter(
        (r) =>
          isSnailMailForRow(r) &&
          r.labVisit.status === "pendingRequisition",
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    awaitingSnailMailRequisitions,
    crmUsers,
    biometrics,
    crmUserMap,
    labVisitMap,
    priceMap,
    awaitingSnailMailBiometricsMap,
    isSnailMailForRow,
  ]);

  const sortedRecentRows = useMemo(() => {
    return [...recentRows].sort((a, b) => {
      const dir = recentSort.asc ? 1 : -1;
      switch (recentSort.col) {
        case "name":
          return (
            [a.crmUser.givenName, a.crmUser.familyName]
              .filter(Boolean)
              .join(" ")
              .localeCompare(
                [b.crmUser.givenName, b.crmUser.familyName]
                  .filter(Boolean)
                  .join(" "),
                "nb",
              ) * dir
          );
        case "lab":
          return (
            (a.crmUser.labName ?? "").localeCompare(
              b.crmUser.labName ?? "",
              "nb",
            ) * dir
          );
        case "bio":
          return (a.biomarkerNames.length - b.biomarkerNames.length) * dir;
        case "cost":
          return (a.cost - b.cost) * dir;
        case "date":
        default: {
          const at = a.requisition.submittedAt
            ? new Date(a.requisition.submittedAt).getTime()
            : 0;
          const bt = b.requisition.submittedAt
            ? new Date(b.requisition.submittedAt).getTime()
            : 0;
          return (at - bt) * dir;
        }
      }
    });
  }, [recentRows, recentSort]);

  const openImpersonateDialog = (row: RequisitionRow) => {
    const authUserId = row.crmUser.authUserId ?? row.crmUser.id ?? "";
    const fullName =
      row.crmUser.fullName ||
      [row.crmUser.givenName, row.crmUser.familyName]
        .filter(Boolean)
        .join(" ")
        .trim() ||
      "Ukjent";
    setReason("");
    setReasonDialogRow({
      authUserId,
      fullName,
      email: row.crmUser.email ?? "",
      actions: [],
      oldestActionDate: Infinity,
    });
  };

  const handleRowClick = (row: UserRow) => {
    if (!row.authUserId) return;
    setReason("");
    setReasonDialogRow(row);
  };

  const handleConfirmImpersonate = async (selectedReason: string) => {
    const reason = selectedReason.trim();
    if (!reasonDialogRow || impersonating || !reason) return;
    const { authUserId } = reasonDialogRow;
    setReasonDialogRow(null);
    setImpersonating(authUserId);
    try {
      await impersonate(authUserId, reason);
      navigate(`/anamnesis/${authUserId}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunne ikke impersonere bruker");
    } finally {
      setImpersonating(null);
    }
  };

  const handleStatusChange = async (
    id: string,
    status: "requisitioned" | "requestRejected",
  ) => {
    try {
      await updateLabVisit.mutateAsync({ id, data: { status } });
      toast.success(
        status === "requisitioned"
          ? "Markert som rekvirert"
          : "Forespørsel avvist",
      );
    } catch (e) {
      // Surface the lifecycle guard's own message — see the same handler in
      // pages/anamnesis/user-detail.tsx.
      toast.error(
        e instanceof Error && e.message
          ? e.message
          : "Kunne ikke endre status",
      );
    }
  };

  const isLoading =
    !isMessagesTab &&
    (actionsLoading ||
      crmLoading ||
      (activeTab === "rekvirering" && readyLoading));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-admin-text-subtle" />
      </div>
    );
  }

  const apiError = isMessagesTab
    ? null
    : actionsError || crmError || searchError;
  if (apiError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <AlertCircle className="h-8 w-8 text-admin-danger-500" />
        <p className="text-sm text-admin-danger-400 font-medium">
          Kunne ikke laste data
        </p>
        <pre className="text-xs text-admin-text-subtle bg-admin-card p-3 rounded max-w-lg overflow-auto whitespace-pre-wrap">
          {apiError instanceof Error
            ? apiError.message
            : JSON.stringify(apiError, null, 2)}
        </pre>
      </div>
    );
  }

  // requisitionRows is empty until both of these have arrived, and an empty
  // table otherwise claims there is nothing in the queue. The full-page
  // spinner above already covers the requisitions themselves.
const requisitionRowsPending =
    readyVisitIds.length > 0 &&
    (biometrics === undefined ||
      readyVisitIds.some(
        (id) => !readyLabVisits?.some((visit) => visit.id === id),
      ));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Kundebehandling</h2>
        <div className="flex items-center gap-3">
          {isRekvireringTab && (
            <Button
              size="sm"
              onClick={() => setApiModalOpen(true)}
              disabled={requisitionRows.length === 0}
            >
              <FileText className="h-4 w-4 mr-1" />
              API-rekvirering ({requisitionRows.length})
            </Button>
          )}
          <span className="text-sm text-admin-text-subtle">
            {isRekvireringTab
              ? `${requisitionRows.length} rekvisisjoner`
              : isMessagesTab
                ? `${awaitingReplyCount} venter på svar`
                : `${filtered.length} kunder`}
          </span>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-admin-card-border">
        {TABS.filter((tab) => tab.key !== "meldinger" || canSeeMessages).map(
          (tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                "px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px cursor-pointer",
                activeTab === tab.key
                  ? "border-admin-primary text-admin-primary"
                  : "border-transparent text-admin-text-secondary hover:text-admin-text-primary hover:border-admin-card-border",
              )}
            >
              {tab.label}
              {tab.key === "meldinger" && awaitingReplyCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-admin-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-admin-primary-fg align-middle">
                  {awaitingReplyCount}
                </span>
              )}
            </button>
          ),
        )}
      </div>

      {/* Rekvirering tab: show requisition-specific table */}
      {isRekvireringTab ? (
        <>
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <RequisitionTableHeaders />
                </thead>
                <tbody>
                  {groupRowsByVisit(requisitionRows).map((row) => (
                    <tr
                      key={row.requisition.id}
                      className={`${groupedRowClasses(row.groupPosition, "border-admin-card-border")} hover:bg-admin-card/50`}
                    >
                      <RequisitionDataCells
                        row={row}
                        impersonating={impersonating}
                        onKundeClick={(r) =>
                          openImpersonateDialog(r)
                        }
                        onBiomarkerClick={setBiomarkerModalNames}
                        splitVisitIds={splitVisitIds}
                        isSnailMail={isSnailMailForRow(row)}
                      />
                      <td className="px-2 py-1.5 text-right">
                        <button
                          className="p-1.5 rounded hover:bg-admin-card/80 text-admin-text-subtle hover:text-admin-text-primary"
                          onClick={() => setActionModalRow(row)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {requisitionRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={9}
                        className="p-8 text-center text-admin-text-subtle"
                      >
                        {requisitionRowsPending ? (
                          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                        ) : (
                          "Ingen kunder venter på rekvirering"
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Awaiting manual confirmation (snail-mail labs) */}
          {awaitingManualConfirmationRows.length > 0 && (
            <>
              <div className="flex items-center gap-3 mt-4">
                <h3 className="text-lg font-medium text-admin-warn-300">
                  Venter på manuell utsendelse (brevpost)
                </h3>
                <Badge
                  variant="outline"
                  className="border-admin-warn-400/40 text-admin-warn-300"
                >
                  {awaitingManualConfirmationRows.length}
                </Badge>
              </div>
              <p className="text-sm text-admin-text-secondary -mt-1">
                Skjemaet må skrives ut og sendes per brev. Klikk «Sett
                rekvirert» når posten er sendt.
              </p>
              <Card className="border-admin-warn-400/40 bg-admin-warn-400/5">
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead>
                      <RequisitionTableHeaders
                        actionLabel="Handlinger"
                        borderClass="border-admin-warn-400/30"
                      />
                    </thead>
                    <tbody>
                      {groupRowsByVisit(awaitingManualConfirmationRows).map(
                        (row) => (
                          <tr
                            key={row.requisition.id}
                            className={groupedRowClasses(
                              row.groupPosition,
                              "border-admin-warn-400/20",
                            )}
                          >
                            <RequisitionDataCells
                              row={row}
                              impersonating={impersonating}
                              onKundeClick={(r) =>
                                openImpersonateDialog(r)
                              }
                              onBiomarkerClick={setBiomarkerModalNames}
                              splitVisitIds={splitVisitIds}
                              isSnailMail={isSnailMailForRow(row)}
                            />
                            <td className="px-2 py-1.5 text-right">
                              <div className="flex justify-end gap-1.5">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEtterrekvireringRow(row)}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Tillegg
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => setActionModalRow(row)}
                                >
                                  Sett rekvirert
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </>
          )}

          {/* Recently requisitioned */}
          {recentRows.length > 0 && (
            <>
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-medium text-admin-text-subtle">
                  Nylig rekvirert
                </h3>
                <Badge variant="outline">{recentRows.length}</Badge>
              </div>
              <Card>
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead>
                      <RequisitionTableHeaders
                        actionLabel="Handlinger"
                        sort={{ state: recentSort, toggle: toggleSort }}
                      />
                    </thead>
                    <tbody>
                      {groupRowsByVisit(sortedRecentRows).map((row) => (
                        <tr
                          key={row.requisition.id}
                          className={groupedRowClasses(
                            row.groupPosition,
                            "border-admin-card-border",
                          )}
                        >
                          <RequisitionDataCells
                            row={row}
                            impersonating={impersonating}
                            onKundeClick={(r) =>
                              openImpersonateDialog(r)
                            }
                            onBiomarkerClick={setBiomarkerModalNames}
                            splitVisitIds={splitVisitIds}
                            isSnailMail={isSnailMailForRow(row)}
                          />
                          <td className="px-2 py-1.5 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEtterrekvireringRow(row)}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Tillegg
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </>
          )}
        </>
      ) : isMessagesTab ? (
        <DoctorMessagesTab />
      ) : (
        /* All other tabs: standard anamnesis list */
        <>
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-admin-text-subtle" />
              <Input
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Søk på navn eller e-post..."
                className="pl-9"
              />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={showOnlyNeedsAction}
                onChange={(e) => setShowOnlyNeedsAction(e.target.checked)}
                className="rounded border-admin-card-border"
              />
              <span className="text-admin-text-secondary">
                Vis kun kunder som trenger handling
              </span>
            </label>
          </div>

          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-admin-card-border">
                    <th className="text-left text-xs font-medium text-admin-text-subtle p-3">
                      Navn
                    </th>
                    <th className="text-left text-xs font-medium text-admin-text-subtle p-3">
                      E-post
                    </th>
                    <th className="text-left text-xs font-medium text-admin-text-subtle p-3">
                      Handlinger
                    </th>
                    {isBloodTestTab && (
                      <>
                        <th className="text-left text-xs font-medium text-admin-text-subtle p-3">
                          Bedrift
                        </th>
                        <th className="text-left text-xs font-medium text-admin-text-subtle p-3">
                          Helseskjema
                        </th>
                      </>
                    )}
                    <th className="text-left text-xs font-medium text-admin-text-subtle p-3">
                      Aktiv rekvisisjon
                    </th>
                    <th className="text-left text-xs font-medium text-admin-text-subtle p-3">
                      Notater
                    </th>
                    <th className="text-left text-xs font-medium text-admin-text-subtle p-3">
                      Sist oppdatert
                    </th>
                    <th className="text-left text-xs font-medium text-admin-text-subtle p-3">
                      Sett av
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <tr
                      key={row.authUserId}
                      className="border-b border-admin-card-border last:border-0 hover:bg-admin-card/50 cursor-pointer"
                      onClick={() => handleRowClick(row)}
                    >
                      <td className="p-3">
                        <span className="font-medium">
                          <MaskedText value={row.fullName} />
                          {impersonating === row.authUserId && (
                            <Loader2 className="inline ml-2 h-3 w-3 animate-spin" />
                          )}
                        </span>
                      </td>
                      <td className="p-3 text-sm text-admin-text-secondary">
                        {row.email ? (
                          <MaskedText value={row.email} type="email" />
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {row.actions.length > 0 ? (
                            row.actions.map((a, i) => (
                              <span
                                key={`${a.id}-${i}`}
                                className="inline-flex gap-0.5 border border-admin-card-border rounded-lg"
                              >
                                <Badge variant={getStatusVariant(a.status)}>
                                  {getStatusText(a.status)}
                                  {a.labVisitId &&
                                    b2bBadgeLabels.has(a.labVisitId) && (
                                      <div className="text-xs pl-1 font-bold">
                                        ({b2bBadgeLabels.get(a.labVisitId)})
                                      </div>
                                    )}
                                </Badge>
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-admin-text-subtle">
                              Ingen
                            </span>
                          )}
                        </div>
                      </td>
                      {isBloodTestTab && (
                        <>
                          <td className="p-3 text-sm text-admin-text-secondary">
                            {bloodTestCompanyNames(row.actions).join(", ") || "—"}
                          </td>
                          <td className="p-3">
                            <span
                              className={cn(
                                "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap",
                                row.healthSurveyCompleted
                                  ? "bg-admin-mint-500/10 text-admin-mint-400"
                                  : "bg-admin-text-subtle/10 text-admin-text-subtle",
                              )}
                            >
                              {surveyCompletionLabel(row.healthSurveyCompleted)}
                            </span>
                          </td>
                        </>
                      )}
                      <td className="p-3">
                        {(() => {
                          const lv = requisitionedByUserId.get(row.authUserId);
                          if (!lv)
                            return (
                              <span className="text-sm text-admin-text-subtle">
                                –
                              </span>
                            );
                          return (
                            <div className="flex flex-wrap gap-1">
                              <Badge variant="default">Rekvirert</Badge>
                              {lv.helfo ? (
                                <Badge variant="outline">Helfo</Badge>
                              ) : null}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="p-3">
                        {(() => {
                          const userNotes = commentsByUserId.get(
                            row.authUserId,
                          );
                          const labNotes = row.actions.flatMap((a) =>
                            a.labVisitId
                              ? (notesByLabVisitId.get(a.labVisitId) ?? [])
                              : [],
                          );
                          const hasNotes =
                            (userNotes && userNotes.length > 0) ||
                            labNotes.length > 0;
                          if (!hasNotes)
                            return (
                              <span className="text-sm text-admin-text-subtle italic">
                                Ingen notater
                              </span>
                            );
                          return (
                            <div className="flex flex-col gap-1.5">
                              {userNotes && userNotes.length > 0 && (
                                <div>
                                  <span className="text-[10px] font-semibold text-admin-text-subtle uppercase">
                                    Brukernotat
                                  </span>
                                  {userNotes.map((c) => (
                                    <p
                                      key={c.id}
                                      className="text-sm text-admin-text-secondary line-clamp-2"
                                    >
                                      {c.comment}
                                    </p>
                                  ))}
                                </div>
                              )}
                              {labNotes.length > 0 && (
                                <div>
                                  <span className="text-[10px] font-semibold text-admin-text-subtle uppercase">
                                    Labnotat
                                  </span>
                                  {labNotes.map((n) => (
                                    <p
                                      key={n.id}
                                      className="text-sm text-admin-text-secondary line-clamp-2"
                                    >
                                      {n.note}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="p-3 text-sm text-admin-text-subtle">
                        {row.actions.length > 0 &&
                        row.oldestActionDate < Infinity
                          ? timeAgo(
                              new Date(row.oldestActionDate).toISOString(),
                            )
                          : "-"}
                      </td>
                      <td className="p-3 text-sm text-admin-text-subtle">
                        {(() => {
                          const imp = impersonatorsByUser?.get(row.authUserId);
                          if (!imp) return null;
                          return (
                            <span title={imp.adminName}>
                              {imp.adminName.split(" ")[0]} ·{" "}
                              {timeAgo(imp.lastViewedAt)}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td
                        colSpan={isBloodTestTab ? 9 : 7}
                        className="p-8 text-center text-admin-text-subtle"
                      >
                        Ingen kunder funnet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {activeTab === "analyse" && (
            <ApprovedAssessmentsTable
              onRowClick={(authUserId, patientName) => {
                setReason("");
                setReasonDialogRow({
                  authUserId,
                  fullName: patientName,
                  email: "",
                  actions: [],
                  oldestActionDate: Infinity,
                });
              }}
            />
          )}
        </>
      )}

      {/* Impersonation reason dialog */}
      <Dialog
        open={!!reasonDialogRow}
        onOpenChange={(open) => {
          if (!open) setReasonDialogRow(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Grunn for impersonering</DialogTitle>
          </DialogHeader>
          {reasonDialogRow && (
            <div className="space-y-3">
              <p className="text-sm text-admin-text-secondary">
                Impersonerer{" "}
                <span className="font-medium text-admin-text-primary">
                  <MaskedText value={reasonDialogRow.fullName} />
                </span>
              </p>
              <ImpersonationReasonPicker
                value={reason}
                onChange={setReason}
                onConfirm={handleConfirmImpersonate}
                disabled={!!impersonating}
              />
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="ghost"
                  onClick={() => setReasonDialogRow(null)}
                >
                  Avbryt
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* API Rekvirering Modal */}
      <ApiRekvireringModal
        open={apiModalOpen}
        onOpenChange={setApiModalOpen}
        users={requisitionRows}
        isSnailMailForRow={isSnailMailForRow}
      />

      {/* Etterrekvirering Modal */}
      {etterrekvireringRow && (
        <EtterrekvireringModal
          open={!!etterrekvireringRow}
          onOpenChange={(open) => {
            if (!open) setEtterrekvireringRow(null);
          }}
          requisitionId={etterrekvireringRow.requisition.id}
          userName={[
            etterrekvireringRow.crmUser.givenName,
            etterrekvireringRow.crmUser.familyName,
          ]
            .filter(Boolean)
            .join(" ")}
          allBiometrics={biometrics ?? []}
        />
      )}

      {/* Rekvirering Action Modal */}
      <Dialog
        open={!!actionModalRow}
        onOpenChange={(open) => {
          if (!open) setActionModalRow(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionModalRow && (
                <MaskedText
                  value={[
                    actionModalRow.crmUser.givenName,
                    actionModalRow.crmUser.familyName,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                />
              )}
            </DialogTitle>
          </DialogHeader>
          {actionModalRow && (
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-admin-text-subtle">Lab:</span>
                <span>{actionModalRow.crmUser.labName ?? "Ikke valgt"}</span>
                {isSnailMailForRow(actionModalRow) && (
                  <Badge
                    variant="outline"
                    className="text-xs text-admin-warn-400 border-admin-warn-400/40"
                  >
                    Brevpost
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-admin-text-subtle">Adresse:</span>
                <span>
                  {getAddress(
                    actionModalRow.crmUser,
                    isSnailMailForRow(actionModalRow)
                  )}
                </span>
              </div>
              {(() => {
                const submittedAt = actionModalRow.requisition.submittedAt;
                return submittedAt ? (
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className="text-xs text-admin-warn-400 border-admin-warn-400/40"
                    >
                      FURST sendt
                    </Badge>
                    <span className="text-admin-text-subtle">
                      {new Date(submittedAt).toLocaleString("nb-NO")}
                    </span>
                  </div>
                ) : null;
              })()}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              size="sm"
              variant="ghost"
              disabled={updateLabVisit.isPending}
              onClick={async () => {
                if (!actionModalRow) return;
                await handleStatusChange(
                  actionModalRow.labVisit.id,
                  "requestRejected",
                );
                setActionModalRow(null);
              }}
            >
              Avvis
            </Button>
            <Button
              size="sm"
              disabled={updateLabVisit.isPending}
              onClick={async () => {
                if (!actionModalRow) return;
                await handleStatusChange(
                  actionModalRow.labVisit.id,
                  "requisitioned",
                );
                setActionModalRow(null);
              }}
            >
              Sett rekvirert
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Biomarkører Modal */}
      <Dialog
        open={!!biomarkerModalNames}
        onOpenChange={(open) => {
          if (!open) setBiomarkerModalNames(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Biomarkører ({biomarkerModalNames?.length ?? 0})
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap gap-1.5 max-h-[400px] overflow-y-auto">
            {biomarkerModalNames?.map((name) => (
              <span
                key={name}
                className="text-xs bg-admin-card border border-admin-card-border px-2 py-1 rounded text-admin-text-secondary"
              >
                {name}
              </span>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── API Rekvirering Modal ────────────────────────────────────

function ApiRekvireringModal({
  open,
  onOpenChange,
  users,
  isSnailMailForRow,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  users: RequisitionRow[];
  isSnailMailForRow: (row: RequisitionRow) => boolean;
}) {
  const sendRequisition = useSendRequisition();
  const qc = useQueryClient();
  const [cookieKey, setCookieKey] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [completed, setCompleted] = useState<Set<string>>(new Set());
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [errors, setErrors] = useState<{ user: string; error: string }[]>([]);
  const confirmNotReceived = useConfirmFurstNotReceived();
  const { hasRole } = useAuth();
  const canConfirmResend = hasRole("doctor") || hasRole("admin");
  // Per-visit corrections to the address FURST pulls from folkeregister, which
  // is occasionally stale enough that FURST rejects the requisition outright.
  // Keyed by lab visit id, sent with that visit only, never persisted.
  const [addressOverrides, setAddressOverrides] = useState<
    Map<string, FurstAddressOverride>
  >(new Map());
  const [expandedOverrides, setExpandedOverrides] = useState<Set<string>>(
    new Set(),
  );

  // Send is per-lab-visit (sendRequisition POSTs every ready partition for the
  // visit), so dedupe rows: one HELFO + one non-HELFO requisition on the same
  // visit should appear as a single bulk-send entry.
  const uniqueByVisit = useMemo(() => {
    const seen = new Set<string>();
    const out: RequisitionRow[] = [];
    for (const u of users) {
      if (seen.has(u.labVisit.id)) continue;
      seen.add(u.labVisit.id);
      out.push(u);
    }
    return out;
  }, [users]);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setSelected(new Set(uniqueByVisit.map((u) => u.labVisit.id)));
      setCompleted(new Set());
      setCurrentId(null);
      setProgress({ current: 0, total: 0 });
      setErrors([]);
      setAddressOverrides(new Map());
      setExpandedOverrides(new Set());
    }
    if (!next && isProcessing) return;
    onOpenChange(next);
  };

  const toggleOverrideForm = (labVisitId: string) => {
    setExpandedOverrides((prev) => {
      const next = new Set(prev);
      if (next.has(labVisitId)) next.delete(labVisitId);
      else next.add(labVisitId);
      return next;
    });
  };

  const setOverrideField = (
    labVisitId: string,
    field: keyof FurstAddressOverride,
    value: string,
  ) => {
    setAddressOverrides((prev) => {
      const next = new Map(prev);
      next.set(labVisitId, { ...next.get(labVisitId), [field]: value });
      return next;
    });
  };

  const allSelected =
    selected.size === uniqueByVisit.length && uniqueByVisit.length > 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(uniqueByVisit.map((u) => u.labVisit.id)));
    }
  };

  const toggleUser = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const resendBlocked = users.some(
    ({ labVisit, requisition }) =>
      selected.has(labVisit.id) &&
      !completed.has(labVisit.id) &&
      ["failed", "inFlight", "interrupted"].includes(furstSendState(requisition)),
  );

  const handleConfirmNotReceived = (labVisitId: string, attemptId: string) => {
    if (
      !window.confirm(
        "Har du sjekket i Fürst at denne rekvisisjonen IKKE er mottatt?\n\nEtter bekreftelsen kan den sendes på nytt, endres eller kanselleres. Bekreftelsen lagres med ditt navn.",
      )
    )
      return;
    confirmNotReceived.mutate(
      { id: labVisitId, attemptId },
      {
        onSuccess: () => toast.success("Bekreftet: ikke mottatt i Fürst"),
        onError: (err) => toast.error(err.message),
      },
    );
  };

  const handleStart = async () => {
    if (!cookieKey.trim()) {
      toast.error("Vennligst skriv inn cookie key");
      return;
    }
    // Skip anything already sent. The loop below stops on the first failure,
    // so a re-run after fixing an address would otherwise re-POST the ones
    // that succeeded — and those have no readyForSubmission requisitions left,
    // so the backend rejects them with "no requisition ready to send".
    const selectedUsers = uniqueByVisit.filter(
      (u) => selected.has(u.labVisit.id) && !completed.has(u.labVisit.id),
    );
    if (selectedUsers.length === 0) {
      toast.error("Ingen brukere valgt");
      return;
    }
    if (resendBlocked) {
      toast.error("En tidligere sending må avklares. Lege må sjekke i Fürst og bekrefte at rekvisisjonen ikke er mottatt.");
      return;
    }
    // Stop a malformed postnummer here rather than spending a FURST
    // round-trip to be told the same thing in vaguer terms. Expand the
    // offending rows so the field to fix is on screen.
    const invalidRows = selectedUsers.filter((u) =>
      postnummerError(addressOverrides.get(u.labVisit.id)),
    );
    if (invalidRows.length > 0) {
      setExpandedOverrides(
        (prev) =>
          new Set([...prev, ...invalidRows.map((u) => u.labVisit.id)]),
      );
      toast.error("Postnummer må være fire siffer");
      return;
    }

    setIsProcessing(true);
    setProgress({ current: 0, total: selectedUsers.length });
    setErrors([]);

    try {
      for (const [i, row] of selectedUsers.entries()) {
        const userName = [row.crmUser.givenName, row.crmUser.familyName]
          .filter(Boolean)
          .join(" ");
        setCurrentId(row.labVisit.id);
        setProgress({ current: i + 1, total: selectedUsers.length });

        try {
          await sendRequisition.mutateAsync({
            id: row.labVisit.id,
            furstCookie: cookieKey,
            addressOverride: nonEmptyOverride(
              addressOverrides.get(row.labVisit.id),
            ),
          });
          setCompleted((prev) => new Set([...prev, row.labVisit.id]));
          setCurrentId(null);
        } catch (err: unknown) {
          setCurrentId(null);
          const message = err instanceof Error ? err.message : "Ukjent feil";
          setErrors((prev) => [...prev, { user: userName, error: message }]);
          // Open the failed row's address fields — a stale postnummer from
          // folkeregister is the usual cause, and that's where the fix goes.
          setExpandedOverrides((prev) => new Set([...prev, row.labVisit.id]));
          setIsProcessing(false);
          toast.error(`Feil ved rekvirering for ${userName}`);
          return;
        }
      }

      setIsProcessing(false);
      toast.success(`${selectedUsers.length} brukere rekvirert`);
    } finally {
      // Refresh the queue once, not after each visit's fan-out.
      // Failed sends now change durable send state too. Refresh on every
      // outcome, including a lost response or a partial split-visit success.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["lab-visits"] }),
        qc.invalidateQueries({ queryKey: ["lab-visit-requisitions"] }),
        qc.invalidateQueries({ queryKey: ["users-needing-action"] }),
      ]);
    }
  };

  const allDone = !isProcessing && completed.size > 0 && errors.length === 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>API-rekvirering</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-admin-text-secondary">
          {uniqueByVisit.length} brukere tilgjengelig for digital rekvirering
        </p>

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-admin-text-primary">
            Cookie Key
          </label>
          <Textarea
            value={cookieKey}
            onChange={(e) => setCookieKey(e.target.value)}
            placeholder="Lim inn FURST cookie key her..."
            disabled={isProcessing}
            className="min-h-[80px]"
          />
        </div>

        {isProcessing && (
          <div className="rounded-md bg-admin-primary/10 border border-admin-primary/20 p-3">
            <p className="text-sm font-medium">
              Prosesserer: {progress.current} av {progress.total}
            </p>
          </div>
        )}

        {errors.length > 0 && (
          <div className="rounded-md bg-admin-negative/10 border border-admin-negative/20 p-3 space-y-3">
            <p className="text-sm font-medium text-admin-negative">
              Feil oppstod:
            </p>
            {errors.map((err, i) => (
              <p
                key={i}
                className="text-xs text-admin-text-secondary break-words"
              >
                {err.user}: {err.error}
              </p>
            ))}
            <p className="text-xs text-admin-text-subtle">
              En feil betyr ikke at Fürst ikke mottok rekvisisjonen. Selv etter
              adressefeil må lege sjekke i Fürst før ny sending. Ikke send igjen
              hvis rekvisisjonen finnes der.
            </p>
          </div>
        )}

        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer border-b border-admin-card-border pb-2">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleSelectAll}
              disabled={isProcessing}
              className="rounded border-admin-card-border"
            />
            <span className="font-medium">Velg alle</span>
          </label>

          <div className="max-h-[300px] overflow-y-auto space-y-1">
            {uniqueByVisit.map((row) => {
              const id = row.labVisit.id;
              const isCompleted = completed.has(id);
              const isCurrent = currentId === id;
              const userName = [row.crmUser.givenName, row.crmUser.familyName]
                .filter(Boolean)
                .join(" ");

              const override = addressOverrides.get(id) ?? {};
              const hasOverride = !!nonEmptyOverride(override);
              const isExpanded = expandedOverrides.has(id);
              const overrideError = postnummerError(override);

              return (
                <div
                  key={id}
                  className={`rounded-md text-sm transition-colors ${
                    isCompleted
                      ? "bg-admin-success-500/10 border border-admin-success-500/20"
                      : isCurrent
                        ? "bg-admin-primary/10 border border-admin-primary/20"
                        : "border border-transparent hover:bg-admin-card/50"
                  }`}
                >
                  <div className="flex items-center gap-3 p-2">
                    {/* The label wraps only the checkbox and name — the address
                        inputs below must not toggle selection when clicked. */}
                    <label className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer">
                      {isCompleted ? (
                        <Check className="h-4 w-4 text-admin-success-400 shrink-0" />
                      ) : isCurrent ? (
                        <Loader2 className="h-4 w-4 animate-spin text-admin-primary shrink-0" />
                      ) : (
                        <input
                          type="checkbox"
                          checked={selected.has(id)}
                          onChange={() => toggleUser(id)}
                          disabled={isProcessing}
                          className="rounded border-admin-card-border shrink-0"
                        />
                      )}
                      <div className="min-w-0">
                        <span
                          className={`font-medium ${isCompleted ? "text-admin-success-300" : ""}`}
                        >
                          <MaskedText value={userName} />
                        </span>
                        <span className="text-xs text-admin-text-subtle ml-2">
                          {row.crmUser.labName ?? "Ukjent lab"}
                        </span>
                        {isSnailMailForRow(row) && (
                          <Badge
                            variant="outline"
                            className="ml-1 text-xs text-admin-warn-400 border-admin-warn-400/40"
                          >
                            Brevpost
                          </Badge>
                        )}
                        {hasOverride && (
                          <Badge
                            variant="outline"
                            className="ml-1 text-xs text-admin-info border-admin-info/40"
                          >
                            Adresse overstyrt
                          </Badge>
                        )}
                      </div>
                    </label>

                    {!isCompleted && (
                      <button
                        type="button"
                        onClick={() => toggleOverrideForm(id)}
                        disabled={isProcessing}
                        className="shrink-0 text-xs text-admin-text-subtle hover:text-admin-text-primary underline disabled:opacity-50"
                      >
                        {isExpanded ? "Skjul adresse" : "Overstyr adresse"}
                      </button>
                    )}
                  </div>

                  {!isCompleted &&
                    users
                      .filter((u) => u.labVisit.id === id && u.requisition.furstSendAttemptId)
                      .map(({ requisition }) => (
                        <div
                          key={requisition.furstSendAttemptId}
                          className="mx-2 mb-2 rounded border border-admin-warn-400/40 bg-admin-warn-400/10 p-2 text-xs space-y-2"
                        >
                          <p>
                            {requisition.helfo ? "HELFO" : "Privat"}: Sending forsøkt —
                            kan allerede være mottatt hos Fürst.
                          </p>
                          <FurstAttemptNotice
                            state={furstSendState(requisition)}
                            canConfirm={canConfirmResend}
                            disabled={isProcessing || confirmNotReceived.isPending}
                            onConfirm={() =>
                              handleConfirmNotReceived(id, requisition.furstSendAttemptId!)
                            }
                          />
                        </div>
                      ))}

                  {/* FURST takes the patient address from its own folkeregister
                      lookup, which is occasionally stale enough that FURST
                      rejects the requisition. These fields replace it for this
                      send only — nothing is stored. */}
                  {isExpanded && !isCompleted && (
                    <div className="px-2 pb-2 space-y-2">
                      <p className="text-xs text-admin-text-subtle">
                        Tomme felt beholder verdien fra folkeregisteret.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Input
                          value={override.adresse ?? ""}
                          onChange={(e) =>
                            setOverrideField(id, "adresse", e.target.value)
                          }
                          placeholder="Adresse"
                          disabled={isProcessing}
                          className="h-8 text-xs flex-1 min-w-[140px]"
                        />
                        <Input
                          value={override.postnummer ?? ""}
                          onChange={(e) =>
                            setOverrideField(id, "postnummer", e.target.value)
                          }
                          placeholder="Postnr"
                          inputMode="numeric"
                          disabled={isProcessing}
                          className={`h-8 text-xs w-24 ${
                            overrideError ? "border-admin-negative" : ""
                          }`}
                        />
                        {/* Uppercased on send to match folkeregister, so show
                            it that way while typing too. */}
                        <Input
                          value={override.poststed ?? ""}
                          onChange={(e) =>
                            setOverrideField(id, "poststed", e.target.value)
                          }
                          placeholder="Poststed"
                          disabled={isProcessing}
                          className="h-8 text-xs w-32 uppercase placeholder:normal-case"
                        />
                      </div>
                      {overrideError && (
                        <p className="text-xs text-admin-negative">
                          {overrideError}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-admin-card-border">
          {allDone ? (
            <Button onClick={() => handleOpenChange(false)}>Lukk</Button>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => handleOpenChange(false)}
                disabled={isProcessing}
              >
                Avbryt
              </Button>
              <Button
                onClick={handleStart}
                disabled={
                  isProcessing || !cookieKey.trim() || selected.size === 0 || resendBlocked
                }
              >
                {isProcessing ? "Prosesserer..." : "Start rekvirering"}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Approved Lab Visits Table ────────────────────────────────

function ApprovedAssessmentsTable({
  onRowClick,
}: {
  onRowClick: (authUserId: string, patientName: string) => void;
}) {
  const PAGE_SIZE = 20;
  const [page, setPage] = useState(0);
  const [doctorSearch, setDoctorSearch] = useState("");
  const [patientSearch, setPatientSearch] = useState("");
  const [debouncedPatient, setDebouncedPatient] = useState("");
  const [debouncedDoctor, setDebouncedDoctor] = useState("");
  const patientTimer = useRef<ReturnType<typeof setTimeout>>();
  const doctorTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      clearTimeout(patientTimer.current);
      clearTimeout(doctorTimer.current);
    };
  }, []);

  const handlePatientSearchChange = (value: string) => {
    setPatientSearch(value);
    clearTimeout(patientTimer.current);
    patientTimer.current = setTimeout(() => {
      setDebouncedPatient(value);
      setPage(0);
    }, 300);
  };
  const handleDoctorSearchChange = (value: string) => {
    setDoctorSearch(value);
    clearTimeout(doctorTimer.current);
    doctorTimer.current = setTimeout(() => {
      setDebouncedDoctor(value);
      setPage(0);
    }, 300);
  };

  // Resolve the patient search on the server, then filter visits by the
  // matched ids. `null` means no patient filter; `undefined` means the search
  // has not answered yet, and the visits query waits for it.
  const patientFilter = debouncedPatient.trim();
  const { data: patientHits } = useCrmUserSearch(patientFilter);
  const matchedAuthUserIds = useMemo(() => {
    if (patientFilter.length < 2) return null;
    if (!patientHits) return undefined;
    return patientHits
      .map((u) => u.authUserId)
      .filter((id): id is string => !!id);
  }, [patientHits, patientFilter]);

  // Fetch completed lab visits with pagination and optional filters
  const doctorFilter = debouncedDoctor.trim();
  const { data: labVisitsData, isLoading } = useQuery({
    queryKey: ["approved-lab-visits", page, doctorFilter, matchedAuthUserIds],
    enabled: matchedAuthUserIds !== undefined,
    queryFn: async () => {
      // If patient search is active but no CRM users matched, return empty
      if (matchedAuthUserIds && matchedAuthUserIds.length === 0) return [];

      const query: Record<string, unknown> = {
        "filter[status]": "assessmentCompleted",
        "filter[selfReported]": false,
        order: "updatedAt.desc",
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      };
      if (doctorFilter) {
        query["filter[reviewedByName]"] = `ilike:%${doctorFilter}%`;
      }
      if (matchedAuthUserIds) {
        query["filter[authUserId]"] = `in:${matchedAuthUserIds.join(",")}`;
      }
      const res = await LabVisitsService.getApiLabVisits({
        headers: getAuthHeaders(),
        query,
      });
      if (!Array.isArray(res.data))
        throw new Error("Failed to fetch lab visits");
      return res.data as LabVisit[];
    },
  });

  // Patient names for the visits on this page only
  const visitUserIds = useMemo(
    () => (labVisitsData ?? []).map((lv) => lv.authUserId),
    [labVisitsData],
  );
  const { data: visitUsers } = useCrmUsersByIds(visitUserIds);
  const crmUserMap = useMemo(() => {
    const map = new Map<string, CrmUser>();
    for (const u of visitUsers ?? []) {
      if (u.authUserId) map.set(u.authUserId, u);
    }
    return map;
  }, [visitUsers]);

  // Enrich with patient info
  const rows = useMemo(() => {
    if (!labVisitsData) return [];
    return labVisitsData.map((lv) => {
      const patient = crmUserMap.get(lv.authUserId);
      const patientName = patient
        ? (patient.fullName ??
          ([patient.givenName, patient.familyName].filter(Boolean).join(" ") ||
            "Ukjent"))
        : "Ukjent";
      return { labVisit: lv, patientName };
    });
  }, [labVisitsData, crmUserMap]);

  const hasMore = (labVisitsData?.length ?? 0) >= PAGE_SIZE;

  return (
    <div className="space-y-3 mt-6">
      <h3 className="text-lg font-medium text-admin-text-subtle">
        Godkjente analyser
      </h3>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-admin-text-subtle" />
          <Input
            value={patientSearch}
            onChange={(e) => handlePatientSearchChange(e.target.value)}
            placeholder="Søk pasient..."
            className="pl-9"
          />
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-admin-text-subtle" />
          <Input
            value={doctorSearch}
            onChange={(e) => handleDoctorSearchChange(e.target.value)}
            placeholder="Søk lege..."
            className="pl-9"
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead>
              <tr className="border-b border-admin-card-border">
                <th className="text-left text-xs font-medium text-admin-text-subtle p-3">
                  Pasient
                </th>
                <th className="text-left text-xs font-medium text-admin-text-subtle p-3">
                  Lege
                </th>
                <th className="text-left text-xs font-medium text-admin-text-subtle p-3">
                  Godkjent
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={3} className="p-8 text-center">
                    <Loader2 className="h-4 w-4 animate-spin text-admin-text-subtle inline" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    className="p-8 text-center text-admin-text-subtle"
                  >
                    Ingen godkjente analyser funnet
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const d = new Date(row.labVisit.updatedAt);
                  const dateStr = `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1).toString().padStart(2, "0")}.${d.getFullYear().toString().slice(2)}`;
                  const timeStr = `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
                  return (
                    <tr
                      key={row.labVisit.id}
                      className="border-b border-admin-card-border last:border-0 hover:bg-admin-card/50 cursor-pointer"
                      onClick={() =>
                        onRowClick(row.labVisit.authUserId, row.patientName)
                      }
                    >
                      <td className="p-3 text-sm font-medium">
                        <MaskedText value={row.patientName} />
                      </td>
                      <td className="p-3 text-sm text-admin-text-secondary">
                        {row.labVisit.reviewedByName ?? "—"}
                      </td>
                      <td className="p-3 text-sm text-admin-text-subtle">
                        {dateStr} {timeStr}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-admin-text-subtle">Side {page + 1}</span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
            Forrige
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!hasMore}
            onClick={() => setPage(page + 1)}
          >
            Neste
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function FurstAttemptNotice({
  state,
  canConfirm,
  disabled,
  onConfirm,
}: {
  state: FurstSendState;
  canConfirm: boolean;
  disabled: boolean;
  onConfirm: () => void;
}) {
  if (state === "received")
    return (
      <p>
        Fürst har bekreftet mottak, men lokal status er ikke oppdatert. Neste
        kjøring oppdaterer statusen uten å sende på nytt.
      </p>
    );
  if (state === "inFlight")
    return <p>Sending pågår. Vent noen minutter og oppdater siden.</p>;
  return (
    <div className="space-y-1">
      <p>
        {state === "failed"
          ? "Sendingen feilet, men Fürst kan likevel ha mottatt den."
          : "Sendingen ble avbrutt uten svar fra Fürst."}{" "}
        Lege må sjekke i Fürst før den kan sendes, endres eller kanselleres.
      </p>
      {canConfirm ? (
        <Button size="sm" variant="outline" disabled={disabled} onClick={onConfirm}>
          Ikke mottatt i Fürst
        </Button>
      ) : (
        <p>Be en lege om å sjekke og bekrefte.</p>
      )}
    </div>
  );
}
